import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  User, 
  CreditCard, 
  BookOpen, 
  Calendar, 
  Phone,
  Mail,
  MapPin,
  Users,
  GraduationCap,
  ArrowLeft,
  Shield,
  Briefcase,
  Clock,
  Eye,
  FileText,
  BarChart3
} from 'lucide-react';
import Layout from '../components/Layout';
import api from '../components/api';
import { toast } from 'react-toastify';

// Import des composants d'onglets
import EtudiantInfoTab from '../components/etudiant/EtudiantInfoTab';
import EtudiantFinancesTab from '../components/etudiant/EtudiantFinancesTab';
import EtudiantNotesTab from '../components/etudiant/EtudiantNotesTab';
import EtudiantAssiduiteTab from '../components/etudiant/EtudiantAssiduiteTab';

const DetailsEtudiant = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(true);
  const [etudiant, setEtudiant] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEtudiantDetails();
  }, [id]);

  const fetchEtudiantDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/etudiant/details-etudiants/${id}/`);
      setEtudiant(response.data);
      setError(null);
    } catch (error) {
      setError('Erreur lors du chargement des détails de l\'étudiant');
      toast.error('Erreur lors du chargement des détails de l\'étudiant');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      id: 'info',
      label: 'Informations',
      icon: User,
      component: EtudiantInfoTab,
      description: 'Informations complètes de l\'étudiant'
    },
    {
      id: 'finances',
      label: 'Situation Financière',
      icon: CreditCard,
      component: EtudiantFinancesTab,
      description: 'Paiements et situation financière'
    },
    {
      id: 'notes',
      label: 'Notes & Évaluations',
      icon: BookOpen,
      component: EtudiantNotesTab,
      description: 'Notes d\'examens et d\'évaluations'
    },
    {
      id: 'assiduite',
      label: 'Assiduité',
      icon: Clock,
      component: EtudiantAssiduiteTab,
      description: 'Retards et absences de l\'étudiant'
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;
  const activeTabInfo = tabs.find(tab => tab.id === activeTab);

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
            <span className="ml-3 text-gray-600">Chargement des détails de l'étudiant...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <div className="text-red-600">{error}</div>
            <button 
              onClick={() => navigate('/etudiants')}
              className="mt-4 bg-[#202654] text-white px-4 py-2 rounded-lg hover:bg-[#1a1f45] transition-colors"
            >
              Retour à la liste
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          
          {/* En-tête professionnel */}
          <div className="bg-gradient-to-r from-[#202654] to-[#1a1f45] px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={() => navigate('/etudiants')}
                  className="mr-3 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                >
                  <ArrowLeft className="h-4 w-4 text-white" />
                </button>
                <div className="bg-white/10 p-2 rounded-lg mr-3">
                  <GraduationCap className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">
                    {etudiant?.nom} {etudiant?.prenom}
                  </h1>
                  <p className="text-blue-100 text-sm">
                    Matricule: {etudiant?.matricule || 'Non assigné'} • 
                    Classe: {etudiant?.get_filiere && etudiant?.get_niveau ? 
                      `${etudiant.get_filiere} - ${etudiant.get_niveau}` : 
                      'Non inscrit'}
                  </p>
                </div>
              </div>
              
              <div className="hidden lg:flex items-center space-x-4 text-white/80">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                  <span className="text-xs">Dossier actif</span>
                </div>
                {etudiant?.contact && (
                  <div className="flex items-center">
                    <Phone className="h-3 w-3 mr-1" />
                    <span className="text-xs">{etudiant.contact}</span>
                  </div>
                )}
                {etudiant?.email && (
                  <div className="flex items-center">
                    <Mail className="h-3 w-3 mr-1" />
                    <span className="text-xs">{etudiant.email}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="px-6">
              <nav className="flex space-x-1 overflow-x-auto py-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`group flex flex-col items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ease-in-out whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'bg-[#202654] text-white shadow-md transform scale-105'
                          : 'text-gray-600 hover:text-[#202654] hover:bg-white hover:shadow-sm'
                      }`}
                    >
                      <Icon className={`h-4 w-4 mb-1 ${
                        activeTab === tab.id ? 'text-white' : 'text-gray-400 group-hover:text-[#202654]'
                      }`} />
                      <span className="text-xs">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Breadcrumb */}
          <div className="bg-white px-6 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <span>Étudiants</span>
                  <span className="mx-2">•</span>
                  <span>{etudiant?.nom} {etudiant?.prenom}</span>
                  <span className="mx-2">•</span>
                  <span className="text-[#202654] font-medium">{activeTabInfo?.label}</span>
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {activeTabInfo?.description}
                </h2>
              </div>
              
              <div className="text-right text-xs">
                <p className="text-gray-500">Consultation</p>
                <p className="font-medium text-gray-900">
                  {new Date().toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          </div>

          {/* Contenu principal */}
          <div className="p-4 bg-gray-50">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              {ActiveComponent && <ActiveComponent etudiant={etudiant} etudiantId={id} />}
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default DetailsEtudiant;
